import { ElementType, type ItemDetails, Track } from '@1debit/analytics';
import { useCallback, useEffect, useRef } from 'react';

import { useAnalytics } from './useAnalytics';

import { type AnalyticsId } from '../AnalyticsProvider';

export const useModalOpenChangeAnalytics = ({
	analyticsId,
	defaultOpen,
	onOpenChange,
	open,
	itemDetails,
}: {
	analyticsId: AnalyticsId;
	open?: boolean;
	defaultOpen?: boolean;
	onOpenChange?: (isOpen: boolean) => void;
	itemDetails?: ItemDetails;
}) => {
	const analytics = useAnalytics();
	const isOpenAndControlled = open || defaultOpen;
	const shouldSendAnalyticsOnCloseWhenControlled = useRef(true);

	const handleModalAnalytics = useCallback(
		(isOpen: boolean) => {
			if (analytics && analyticsId && isOpen) {
				analytics.handler.trackViewedFullScreenModal({ unique_id: analyticsId, location: analytics.location });

				analytics.handler.trackEvent(Track.WebItemViewed, {
					unique_id: analyticsId,
					element_type: ElementType.ELEMENT_TYPE_MODAL,
					item_details: itemDetails,
				});
			}
		},
		[analytics, analyticsId, itemDetails],
	);

	useEffect(() => {
		if (isOpenAndControlled) {
			shouldSendAnalyticsOnCloseWhenControlled.current = true;
			const isOpen = true;
			handleModalAnalytics(isOpen);
		}

		return () => {
			if (isOpenAndControlled && shouldSendAnalyticsOnCloseWhenControlled.current === true) {
				const isOpen = false;
				handleModalAnalytics(isOpen);
			}
		};
	}, [handleModalAnalytics, isOpenAndControlled]);

	const handleModalOpenChangeAnalytics = useCallback(
		(isOpen: boolean) => {
			shouldSendAnalyticsOnCloseWhenControlled.current = false;
			handleModalAnalytics(isOpen);
			onOpenChange?.(isOpen);
		},
		[handleModalAnalytics, onOpenChange],
	);

	return {
		handleModalOpenChangeAnalytics,
	};
};
